import { tokenStorage } from '@klickly/front-utils';
import { flow, getParent, types } from 'mobx-state-tree';
import { kcpAPI } from '../shared/constants';

/**
 * Owns the CP-V2 canonical identity token (`u`) for the Distribution Partner SPA.
 *
 * The DP marketplace has no customer-account concept, so — unlike Giftly-consumer —
 * it never called `/auth/me` and therefore never wrote the first-party `_kl_u`
 * token. Without it the klpixel core (which reads `_kl_u` from the shared
 * `@klickly/front-utils` tokenStorage mirror) had no `u` to stamp on `/ui` events,
 * leaving DP orders un-attributable in CP-V2. This store closes that gap: it hits
 * the same `/auth/me` identity chokepoint purely to mint/resolve `u` and mirror it
 * into localStorage / sessionStorage / cookie. It deliberately ignores the customer
 * envelope in the response — DP stays anonymous. Follow-on to KLIC-11325.
 */
export const IdentityStore = types
    .model('IdentityStore', {})
    .views((self) => ({
        get root() {
            return getParent(self);
        }
    }))
    .actions((self) => ({
        /**
         * Resolve the canonical `u` from `/auth/me` and mirror it into the shared
         * `_kl_u` storage slots so the klpixel attaches it to subsequent UI events.
         *
         * Best-effort by contract: any failure (network, CORS, malformed body) is
         * swallowed so identity can never wedge SPA boot — the pixel simply keeps
         * emitting events without `u`, exactly as before this store existed.
         *
         * @returns {Promise<void>}
         */
        syncU: flow(function* syncU() {
            try {
                const payload = yield self.root.get(kcpAPI.auth.me);
                // `write` is a no-op for a missing/malformed value, so an anonymous
                // or partial `/auth/me` response degrades gracefully.
                tokenStorage.write(payload?.u);
            } catch (_) {
                // swallow — identity is best-effort; boot must not depend on it
            }
        })
    }));
